/**
 * Sculpt.ts — turns a SharkGenome into a coloured voxel occupancy grid: a signed-distance
 * generalized cylinder along a curved spine, swept fin slabs, then a quantised pattern pass.
 */

import type { SharkGenome, Snout } from './Genome.ts';
import { bezier4 } from './Genome.ts';
import { SLOT } from './Palette.ts';
import { Simplex3 } from './Noise.ts';

/* ===========================================================================
 * ORIENTATION — non-negotiable (CONTRACTS §9). Everything below obeys this.
 *
 *   z = dims[2]-1  is the NOSE.        z = 0 is the TAIL TIP.      World +Z is forward.
 *   s = (dims[2]-1 - z) / (dims[2]-1)  ->  s = 0 at the nose, s = 1 at the tail.
 *   y = 0 is the BELLY side.           y = dims[1]-1 is above the dorsal fin tip.
 *   x is lateral and the body is symmetric about the centre column x = floor(w/2).
 *   index = x + w * (y + h * z)
 *
 * Internally the shaping parameter is `u`, the BODY spine parameter: u = 0 at the nose
 * voxel, u = 1 at the end of the body spine (the caudal root). u is a linear rescale of s
 * — same direction, same endpoints ordering — and the caudal fin lives at u > 1, behind
 * the body, which is why dims[2] > genome.length. GreedyMesh derives the emitted `spineT`
 * from the solid z bounds, so it lands nose->0 / tail->1 exactly as the shader expects.
 * =========================================================================== */

export interface VoxelVolume {
  /** [w, h, l] = X lateral, Y up, Z spine */
  dims: [number, number, number];
  /** length w*h*l. 0 = empty, else (paletteSlot + 1). */
  data: Uint8Array;
  solidCount: number;
  /** inclusive voxel-space bounds of the solid set */
  min: [number, number, number];
  max: [number, number, number];
}

/** index order is x + w * (y + h * z) — GreedyMesh depends on this byte for byte. */
export function volIndex(
  dims: readonly [number, number, number],
  x: number,
  y: number,
  z: number,
): number {
  return x + dims[0] * (y + dims[1] * z);
}

// Cell values are (slot + 1); 0 is empty. Cached so the hot loops compare integers.
const V_DORSAL = SLOT.DORSAL + 1;
const V_FLANK = SLOT.FLANK + 1;
const V_VENTRAL = SLOT.VENTRAL + 1;
const V_GILL = SLOT.GILL + 1;
const V_TOOTH = SLOT.TOOTH + 1;
const V_EYE = SLOT.EYE + 1;
const V_FIN_EDGE = SLOT.FIN_EDGE + 1;

// ---------------------------------------------------------------------------
// Body shape constants. These are silhouette, not gameplay — they belong here and not in
// data/tuning.ts, which is the systems tunable surface.
// ---------------------------------------------------------------------------

/** u at which the body commits to the tail stalk. Earlier = longer, more graceful taper. */
const TAPER_START = 0.50;
/** CONTRACTS §9.3: the stalk is down to girth*peduncle by here. */
const PEDUNCLE_U = 0.88;
/** x girth. Heterocercal lift — the spine rises into the upper caudal lobe. */
const PEDUNCLE_RISE = 0.30;
/** x girth. A shark's back is not a straight line; a shallow arch over the shoulders. */
const SHOULDER_ARCH = 0.055;
/** u over which headDrop eases out behind the nose. */
const HEAD_DROP_U = 0.32;
/** Extra heightRatio at the stalk: pinched in plan view, deep in profile, so the caudal
 *  has something to grow out of instead of sprouting from a pencil. */
const PEDUNCLE_COMPRESS = 1.30;
/** u of the caudal fin root — just short of the body's tail end so the lobes union in. */
const CAUDAL_ROOT_U = 0.955;
/**
 * Upper/lower caudal lobe elevation in degrees, at rake = 0 and rake = 1. Anatomically a
 * shark's upper lobe rides much steeper than this, but elevation is exactly the axis a
 * 62-degree camera foreshortens away: at 54 degrees the whole tail collapses to a stub.
 * These are pitched for the read, and the heterocercal asymmetry is carried by lobe
 * *length* instead, which the camera does not eat.
 */
const CAUDAL_UP_DEG: [number, number] = [36, 15];
const CAUDAL_DOWN_DEG: [number, number] = [27, 10];

interface SnoutShape {
  /** radius at the tip as a fraction of the radius where the snout meets the body */
  tipFrac: number;
  /** exponent on the nose taper. < 1 holds volume forward (blunt/goblin), > 1 pinches. */
  sharp: number;
  /** floor on snout radius as a fraction of girth (0 = no floor) */
  minFrac: number;
  /** voxels of spherical cap at the very tip, so the nose is not a flat disc */
  capLen: number;
  /** lateral widening at the tip */
  wide: number;
  /** dorsoventral flattening at the tip */
  flat: number;
  /** fraction of the snout, measured from the tip, over which wide/flat ramp in */
  widenFrac: number;
  /** x girth. Protrusible-jaw bulge hanging under the snout (goblin only). */
  underbite: number;
}

const SNOUT_SHAPE: Record<Snout, SnoutShape> = {
  // mako, thresher — needle nose, cross-section stays round
  pointed: { tipFrac: 0.10, sharp: 0.55, minFrac: 0, capLen: 0.6, wide: 1, flat: 1, widenFrac: 0.25, underbite: 0 },
  // great white, greenland — conical and heavy right to the cap
  blunt: { tipFrac: 0.80, sharp: 0.75, minFrac: 0.55, capLen: 1.7, wide: 1.04, flat: 0.94, widenFrac: 0.5, underbite: 0 },
  // tiger, whale — the head reads as a spade from directly above
  shovel: { tipFrac: 0.66, sharp: 0.70, minFrac: 0.42, capLen: 1.4, wide: 1.58, flat: 0.56, widenFrac: 0.34, underbite: 0 },
  // hammerhead — head is pre-flattened so the cephalofoil bar blends instead of butt-joints
  hammer: { tipFrac: 0.58, sharp: 0.80, minFrac: 0.40, capLen: 1.2, wide: 1.18, flat: 0.60, widenFrac: 0.45, underbite: 0 },
  // goblin — a long narrow blade that flares late, plus the jaw bulge
  goblin: { tipFrac: 0.16, sharp: 0.28, minFrac: 0, capLen: 0.7, wide: 1.1, flat: 0.82, widenFrac: 0.3, underbite: 0.5 },
};

// ---------------------------------------------------------------------------

function clamp(v: number, a: number, b: number): number {
  return v < a ? a : v > b ? b : v;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function smoothstep(e0: number, e1: number, x: number): number {
  const d = e1 - e0;
  const t = clamp(d === 0 ? (x < e0 ? 0 : 1) : (x - e0) / d, 0, 1);
  return t * t * (3 - 2 * t);
}

export function sculpt(genome: SharkGenome): VoxelVolume {
  const g = genome;

  const N = Math.max(8, Math.round(g.length)); // body spine voxels, nose -> caudal root
  const span1 = N - 1;
  const girth = Math.max(1.2, g.girth);
  const hrBase = Math.max(0.5, g.heightRatio);
  const ped = clamp(g.peduncle, 0.08, 0.6);
  const gp = clamp(g.girthPeak, 0.08, 0.9);
  const sn = SNOUT_SHAPE[g.snout] ?? SNOUT_SHAPE.pointed;
  const uSnout = clamp(g.snoutLength / N, 0.02, 0.42);
  const headDrop = clamp(g.headDrop, 0, 4);

  // Salted so a shark's pattern noise does not correlate with anything else seeded off the
  // same run seed (arena scatter, spawn order, ...).
  const nz = new Simplex3((g.seed ^ 0x51ed270b) >>> 0);

  // -- taper curve ---------------------------------------------------------
  // The genome hands us four bezier control points whose peak lands wherever it lands. Find
  // that peak numerically, then warp u so the maximum is forced onto girthPeak and the value
  // is normalised to exactly `girth`. This keeps `taperCurve` free to express profile
  // *character* while girthPeak stays the single source of truth for *where* the shark is fat.
  let tPeak = 0.35;
  let vPeak = 0;
  for (let i = 0; i <= 32; i++) {
    const t = i / 32;
    const v = bezier4(g.taperCurve, t);
    if (v > vPeak) {
      vPeak = v;
      tPeak = t;
    }
  }
  if (!(vPeak > 1e-3)) {
    vPeak = 1;
    tPeak = 0.35;
  }
  tPeak = clamp(tPeak, 0.08, 0.92);

  const warp = (u: number): number =>
    u < gp ? (u / gp) * tPeak : tPeak + ((u - gp) / (1 - gp)) * (1 - tPeak);

  const baseRadius = (u: number): number => (girth * bezier4(g.taperCurve, warp(u))) / vPeak;

  const rSnoutSeam = baseRadius(uSnout);
  const snoutFloor = Math.min(girth * sn.minFrac, rSnoutSeam);
  const rPeduncle = Math.max(0.75, girth * ped);
  const capU = sn.capLen / span1;

  const bodyRadius = (u: number): number => {
    let r = baseRadius(u);

    // Commit to the stalk. k*k holds the shoulders full and then falls away hard — a shark
    // that stays fat to the tail reads as a submarine.
    if (u > TAPER_START) {
      const k = smoothstep(TAPER_START, PEDUNCLE_U, u);
      r = lerp(r, rPeduncle, k * k);
    }
    if (u > PEDUNCLE_U) {
      r = rPeduncle * (1 - 0.42 * smoothstep(PEDUNCLE_U, 1, u));
    }

    if (u < uSnout) {
      const k = u / uSnout; // 0 at the tip, 1 where the snout meets the body
      const rTip = rSnoutSeam * sn.tipFrac;
      let rn = rTip + (rSnoutSeam - rTip) * Math.pow(k, sn.sharp);
      if (snoutFloor > 0) rn = Math.max(rn, snoutFloor);
      // Spherical cap so the tip is rounded off rather than sliced flat.
      if (u < capU && capU > 1e-6) {
        const a = 1 - u / capU;
        rn *= Math.sqrt(Math.max(0, 1 - a * a));
      }
      r = rn;
    }

    return Math.max(r, 0.5);
  };

  const spineY = (u: number): number => {
    let y = -headDrop * (1 - smoothstep(0, HEAD_DROP_U, u));
    y += girth * SHOULDER_ARCH * Math.sin(Math.PI * clamp(u, 0, 1));
    y += girth * PEDUNCLE_RISE * smoothstep(0.66, 1, u);
    return y;
  };

  const noseWide = (u: number): number => {
    const ww = uSnout * sn.widenFrac;
    if (u >= ww || ww < 1e-6) return 1;
    return lerp(1, sn.wide, smoothstep(0, 1, 1 - u / ww));
  };

  const noseFlat = (u: number): number => {
    const ww = uSnout * sn.widenFrac;
    if (u >= ww || ww < 1e-6) return 1;
    return lerp(1, sn.flat, smoothstep(0, 1, 1 - u / ww));
  };

  // -- per-slice profile, indexed by i = nose(0) .. caudal root(N-1) --------
  const rArr = new Float32Array(N);
  const yArr = new Float32Array(N);
  const wArr = new Float32Array(N); // lateral scale
  const vArr = new Float32Array(N); // vertical scale (heightRatio * flatten)
  for (let i = 0; i < N; i++) {
    const u = i / span1;
    rArr[i] = bodyRadius(u);
    yArr[i] = spineY(u);
    wArr[i] = noseWide(u);
    vArr[i] = hrBase * noseFlat(u) * lerp(1, PEDUNCLE_COMPRESS, smoothstep(0.62, 0.95, u));
  }

  // -- extents -> grid dims ------------------------------------------------
  const iOf = (u: number): number => clamp(Math.round(u * span1), 0, N - 1);

  const goblinDrop = g.snout === 'goblin' ? girth * sn.underbite : 0;
  let yLo = Infinity;
  let yHi = -Infinity;
  let xHi = 0;
  for (let i = 0; i < N; i++) {
    const hy = rArr[i] * vArr[i];
    if (yArr[i] + hy > yHi) yHi = yArr[i] + hy;
    const lo = yArr[i] - hy - (i / span1 < uSnout ? goblinDrop : 0);
    if (lo < yLo) yLo = lo;
    const hx = rArr[i] * wArr[i];
    if (hx > xHi) xHi = hx;
  }

  const dorsal = g.dorsalFin;
  const pectoral = g.pectoralFins;
  const caudal = g.caudalFin;
  const secondDorsalU = 0.72;
  const analU = 0.78;
  const pelvicU = 0.62;

  const iD = iOf(clamp(dorsal.position, 0.05, 0.95));
  yHi = Math.max(yHi, yArr[iD] + rArr[iD] * vArr[iD] + Math.max(0, dorsal.height) + 1);
  if (g.secondDorsal > 0.5) {
    const i2 = iOf(secondDorsalU);
    yHi = Math.max(yHi, yArr[i2] + rArr[i2] * vArr[i2] + g.secondDorsal + 1);
  }

  const iP = iOf(clamp(pectoral.position, 0.05, 0.95));
  const pecSpan = Math.max(1, pectoral.height);
  yLo = Math.min(yLo, yArr[iP] - rArr[iP] * vArr[iP] * 0.30 - pecSpan * 0.22 - 2);
  xHi = Math.max(xHi, rArr[iP] * wArr[iP] * 0.55 + pecSpan + 1);

  if (g.pelvicFin > 0.5) {
    const iv = iOf(pelvicU);
    yLo = Math.min(yLo, yArr[iv] - rArr[iv] * vArr[iv] - g.pelvicFin - 1);
    xHi = Math.max(xHi, rArr[iv] * wArr[iv] * 0.55 + g.pelvicFin + 1);
    const ia = iOf(analU);
    yLo = Math.min(yLo, yArr[ia] - rArr[ia] * vArr[ia] - g.pelvicFin * 0.8 - 1);
  }

  const upDeg = lerp(CAUDAL_UP_DEG[0], CAUDAL_UP_DEG[1], clamp(caudal.rake, 0, 1));
  const downDeg = lerp(CAUDAL_DOWN_DEG[0], CAUDAL_DOWN_DEG[1], clamp(caudal.rake, 0, 1));
  const upAng = (upDeg * Math.PI) / 180;
  const downAng = (downDeg * Math.PI) / 180;
  const upLen = Math.max(0, caudal.upperLobe);
  const lowLen = Math.max(0, caudal.lowerLobe);
  // Blade chord. A caudal narrower than ~3 voxels projects to a bare line under a 62-degree
  // camera and the whole tail reads as a stick, which is the single loudest silhouette bug.
  const upW = clamp(upLen * 0.62, 2.6, girth * 1.9);
  const lowW = clamp(lowLen * 0.62, 2.2, girth * 1.6);
  const iRoot = iOf(CAUDAL_ROOT_U);
  const yCaudalRoot = yArr[iRoot];
  yHi = Math.max(yHi, yCaudalRoot + upLen * Math.sin(upAng) + upW * 0.5 + 1);
  yLo = Math.min(yLo, yCaudalRoot - lowLen * Math.sin(downAng) - lowW * 0.5 - 1);

  const isHammer = g.snout === 'hammer' && g.foilSpan > 2;
  const foilSpan = isHammer ? Math.min(g.foilSpan, 40) : 0;
  if (isHammer) xHi = Math.max(xHi, foilSpan + 1);

  const caudalBack = Math.ceil(
    Math.max(upLen * Math.cos(upAng) + upW, lowLen * Math.cos(downAng) + lowW, 2) + 1,
  );

  const halfW = Math.max(2, Math.ceil(xHi)) + 1;
  const w = halfW * 2 + 1;
  const cx = halfW;
  const h = Math.max(6, Math.ceil(yHi - yLo) + 3);
  const yOff = 1 - yLo;
  const zTail0 = 1 + caudalBack;
  const zNose = zTail0 + N - 1;
  const l = zNose + 2;

  const data = new Uint8Array(w * h * l);
  const dims: [number, number, number] = [w, h, l];
  const wh = w * h;

  const setVox = (x: number, y: number, z: number, v: number): void => {
    if (x < 0 || y < 0 || z < 0 || x >= w || y >= h || z >= l) return;
    data[x + w * y + wh * z] = v;
  };

  // ======================= 1. body SDF, one pass ==========================
  // d = sqrt((dx/wide)^2 + (dy/vScale)^2) - radius(u), filled where d <= 0. Compared squared
  // to skip the sqrt; the per-slice scalars are hoisted so nothing allocates in the loop.
  for (let i = 0; i < N; i++) {
    const z = zNose - i;
    const r = rArr[i];
    const wide = wArr[i];
    const vsc = vArr[i];
    const cy = yArr[i] + yOff;
    const rx = r * wide;
    const ry = r * vsc;
    const x0 = Math.max(0, Math.ceil(cx - rx));
    const x1 = Math.min(w - 1, Math.floor(cx + rx));
    const y0 = Math.max(0, Math.ceil(cy - ry));
    const y1 = Math.min(h - 1, Math.floor(cy + ry));
    const zBase = wh * z;
    const invV = 1 / vsc;
    const r2 = r * r;
    for (let y = y0; y <= y1; y++) {
      const dy = (y - cy) * invV;
      const rowBase = zBase + w * y;
      const rem = r2 - dy * dy;
      if (rem < 0) continue;
      // Solve the ellipse per row instead of testing every x — halves the body pass.
      const dxMax = Math.sqrt(rem) * wide;
      const xa = Math.max(x0, Math.ceil(cx - dxMax));
      const xb = Math.min(x1, Math.floor(cx + dxMax));
      for (let x = xa; x <= xb; x++) data[rowBase + x] = V_FLANK;
    }
  }

  // -- shared by every detail pass -----------------------------------------

  const solidAt = (x: number, y: number, z: number): boolean =>
    x >= 0 && y >= 0 && z >= 0 && x < w && y < h && z < l && data[x + w * y + wh * z] !== 0;

  /** inclusive lateral run at one (y, z); the workhorse for slab fins */
  const fillSpanX = (xa: number, xb: number, y: number, z: number, v: number): void => {
    if (y < 0 || y >= h || z < 0 || z >= l) return;
    const a = Math.max(0, Math.ceil(xa));
    const b = Math.min(w - 1, Math.floor(xb));
    const base = wh * z + w * y;
    for (let x = a; x <= b; x++) data[base + x] = v;
  };

  /** last solid voxel walking laterally out from the spine; -1 if the row is empty */
  const flankX = (y: number, z: number, dir: number): number => {
    if (!solidAt(cx, y, z)) return -1;
    let x = cx;
    while (solidAt(x + dir, y, z)) x += dir;
    return x === cx ? -1 : x;
  };

  // ======================= 2. snout details ===============================
  // Goblin protrusible jaw: a bulge slung under the forward snout so the underbite is
  // visible in silhouette, which is the entire point of the species.
  if (goblinDrop > 0) {
    const iEnd = iOf(uSnout);
    for (let i = 0; i <= iEnd; i++) {
      const u = i / span1;
      const drop = goblinDrop * (1 - smoothstep(0, uSnout, u));
      if (drop < 0.6) continue;
      const z = zNose - i;
      const cyB = yArr[i] + yOff;
      const ryB = rArr[i] * vArr[i];
      const rxB = Math.max(0.8, rArr[i] * wArr[i] * 0.8);
      const yTop = Math.round(cyB - ryB * 0.35);
      for (let y = Math.round(cyB - ryB - drop); y <= yTop; y++) {
        fillSpanX(cx - rxB, cx + rxB, y, z, V_FLANK);
      }
    }
  }

  // Cephalofoil: a real lateral bar, not a widened nose. Recorded so the eyes can be
  // planted on its outer tips, which is what actually sells a hammerhead from above.
  let foilTipZ = -1;
  let foilTipY = -1;
  if (isHammer) {
    const iHead = iOf(uSnout * 0.5);
    const zH = zNose - iHead;
    const cyH = yArr[iHead] + yOff;
    const ryH = rArr[iHead] * vArr[iHead];
    const chord = Math.max(3, rArr[iHead] * 1.45);
    const steps = Math.max(4, Math.ceil(foilSpan * 2));
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const d = Math.round(t * foilSpan);
      // the leading edge sweeps back toward the tips; a straight bar reads as a plank
      const zc = zH - t * t * chord * 0.6;
      const half = Math.max(1, chord * 0.5 * (1 - 0.3 * t));
      const hy = Math.max(0, Math.round(ryH * 0.4 * (1 - 0.45 * t)));
      const yc = Math.round(cyH);
      for (let z = Math.ceil(zc - half); z <= Math.floor(zc + half); z++) {
        for (let y = yc - hy; y <= yc + hy; y++) {
          setVox(cx + d, y, z, V_FLANK);
          setVox(cx - d, y, z, V_FLANK);
        }
      }
      if (s === steps) {
        foilTipZ = Math.round(zc);
        foilTipY = yc;
      }
    }
  }

  // ======================= 3. fins ========================================
  // Fins are painted dorsal-coloured rather than left for the pattern pass: they are thin
  // slabs whose `rel` height would read as ventral on the pectorals, and a single flat
  // colour merges into a handful of quads instead of dozens.

  /** vertical slab above the back — first and second dorsal */
  const finDorsal = (idx: number, height: number, chord: number, rake: number): void => {
    if (height < 1 || chord < 1) return;
    const zR = zNose - idx;
    const cyF = yArr[idx] + yOff;
    const ryF = rArr[idx] * vArr[idx];
    const yRoot = cyF + ryF - 1;
    const steps = Math.max(2, Math.ceil(height * 2));
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const y = Math.round(yRoot + t * height);
      const half = Math.max(0.6, chord * 0.5 * (1 - 0.62 * t));
      const zc = zR - rake * height * t - chord * 0.16 * t;
      const hx = t < 0.3 ? 1 : 0; // 3 voxels where it meets the body, 1 above — it blends
      const v = t > 0.82 ? V_FIN_EDGE : V_DORSAL;
      for (let z = Math.ceil(zc - half); z <= Math.floor(zc + half); z++) {
        fillSpanX(cx - hx, cx + hx, y, z, v);
      }
    }
  };

  /** mirrored horizontal slabs — the fins that actually read from a top-down camera */
  const finPaired = (idx: number, span: number, chord: number, rake: number, droop: number): void => {
    if (span < 1 || chord < 1) return;
    const zR = zNose - idx;
    const cyF = yArr[idx] + yOff;
    const ryF = rArr[idx] * vArr[idx];
    const rxF = rArr[idx] * wArr[idx];
    const yRoot = cyF - ryF * 0.3;
    const steps = Math.max(3, Math.ceil(span * 2));
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const d = Math.round(rxF * 0.55 + t * span);
      const y = Math.round(yRoot - span * droop * t * t);
      // Pectorals are the fins that actually read from above, so they taper gently and stay
      // two voxels thick well out along the span rather than thinning to a wafer at the root.
      const half = Math.max(0.7, chord * 0.5 * (1 - 0.36 * t));
      const zc = zR - rake * span * 1.15 * t - chord * 0.2 * t;
      const v = t > 0.82 ? V_FIN_EDGE : V_DORSAL;
      const thick = t < 0.45 ? 1 : 0;
      for (let z = Math.ceil(zc - half); z <= Math.floor(zc + half); z++) {
        for (let k = 0; k <= thick; k++) {
          setVox(cx + d, y - k, z, v);
          setVox(cx - d, y - k, z, v);
        }
      }
    }
  };

  /** one heterocercal caudal lobe swept back from the peduncle at `ang` above horizontal */
  const caudalLobe = (len: number, ang: number, wid: number, up: boolean): void => {
    if (len < 1) return;
    const zR = zNose - iRoot;
    const yR = yArr[iRoot] + yOff;
    const dz = -Math.cos(ang);
    const dy = (up ? 1 : -1) * Math.sin(ang);
    const pz = -dy; // perpendicular to the lobe axis, inside the YZ plane
    const py = dz;
    const steps = Math.max(4, Math.ceil(len * 2));
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const az = zR + dz * t * len;
      const ay = yR + dy * t * len;
      const half = Math.max(0.7, wid * 0.5 * (1 - 0.48 * t));
      const ks = Math.max(1, Math.ceil(half * 2));
      const v = t > 0.84 ? V_FIN_EDGE : V_DORSAL;
      const hx = t < 0.18 ? 1 : 0;
      for (let k = -ks; k <= ks; k++) {
        const f = (k / ks) * half;
        fillSpanX(cx - hx, cx + hx, Math.round(ay + py * f), Math.round(az + pz * f), v);
      }
    }
  };

  finDorsal(iD, dorsal.height, dorsal.chord, clamp(dorsal.rake, 0, 1.4));
  if (g.secondDorsal > 0.5) {
    const i2 = iOf(secondDorsalU);
    finDorsal(i2, g.secondDorsal, Math.max(1.5, dorsal.chord * 0.42), 0.8);
  }
  finPaired(iP, pecSpan, Math.max(1.5, pectoral.chord), clamp(pectoral.rake, 0, 1.4), 0.22);
  if (g.pelvicFin > 0.5) {
    finPaired(iOf(pelvicU), g.pelvicFin, Math.max(1.5, g.pelvicFin * 0.85), 0.7, 0.3);
    // the anal fin is single and ventral-midline, so it is neither a dorsal nor a paired slab
    const ia = iOf(analU);
    const zA = zNose - ia;
    const cyA = yArr[ia] + yOff;
    const ryA = rArr[ia] * vArr[ia];
    const hA = g.pelvicFin * 0.8;
    for (let s = 0; s <= Math.ceil(hA * 2); s++) {
      const t = s / Math.max(1, Math.ceil(hA * 2));
      const y = Math.round(cyA - ryA - t * hA);
      const half = Math.max(0.6, hA * 0.5 * (1 - 0.5 * t));
      const zc = zA - hA * 0.55 * t;
      for (let z = Math.ceil(zc - half); z <= Math.floor(zc + half); z++) {
        fillSpanX(cx, cx, y, z, V_DORSAL);
      }
    }
  }
  caudalLobe(upLen, upAng, upW, true);
  caudalLobe(lowLen, downAng, lowW, false);

  // ======================= 4. gills, mouth, teeth, eyes ===================
  // All recolour-only. Carving would punch holes in the flank and shatter the greedy
  // merge; every one of these is a surface voxel whose slot changes.
  {
    const nSlit = Math.round(clamp(g.gillSlits, 3, 8));
    for (let j = 0; j < nSlit; j++) {
      const u = lerp(0.175, 0.305, nSlit === 1 ? 0.5 : j / (nSlit - 1));
      const i = iOf(u);
      const z = zNose - i;
      const cyG = yArr[i] + yOff;
      const ryG = rArr[i] * vArr[i];
      const yA = Math.round(cyG - ryG * 0.4);
      const yB = Math.round(cyG + ryG * 0.28);
      for (let y = yA; y <= yB; y++) {
        const xr = flankX(y, z, 1);
        if (xr >= 0) data[xr + w * y + wh * z] = V_GILL;
        const xl = flankX(y, z, -1);
        if (xl >= 0) data[xl + w * y + wh * z] = V_GILL;
      }
    }
  }

  {
    const iM = iOf(clamp(uSnout * 0.8, 0.02, 0.3));
    const z = zNose - iM;
    const cyM = yArr[iM] + yOff;
    const ryM = rArr[iM] * vArr[iM];
    const half = Math.max(1, Math.min(g.jawWidth * 0.5, rArr[iM] * wArr[iM] * 0.92));
    const yMouth = Math.round(cyM - ryM * 0.6);
    fillSpanX(cx - half, cx + half, yMouth, z, V_GILL);
    fillSpanX(cx - half, cx + half, yMouth, z - 1, V_GILL);
    // A contiguous tooth band, never alternating voxels — a checkerboard grin would cost
    // more triangles than the entire rest of the head.
    const rows = Math.round(clamp(g.toothRows, 1, 3));
    for (let r = 0; r < rows; r++) {
      fillSpanX(cx - half * 0.82, cx + half * 0.82, yMouth + 1 + r, z, V_TOOTH);
    }
  }

  {
    if (g.eyeStyle === 'cephalofoil' && foilTipZ >= 0) {
      for (let dz = -1; dz <= 0; dz++) {
        setVox(cx + foilSpan, foilTipY, foilTipZ + dz, V_EYE);
        setVox(cx - foilSpan, foilTipY, foilTipZ + dz, V_EYE);
      }
    } else {
      const iE = iOf(clamp(uSnout * 1.25, 0.04, 0.35));
      const zE = zNose - iE;
      const cyE = yArr[iE] + yOff;
      const ryE = rArr[iE] * vArr[iE];
      const yE = Math.round(cyE + ryE * 0.28);
      const stalk = g.eyeStyle === 'stalked' ? 1 : 0;
      const xr = flankX(yE, zE, 1);
      if (xr >= 0) {
        setVox(xr, yE, zE, V_EYE);
        if (stalk) setVox(xr + 1, yE, zE, V_EYE);
      }
      const xl = flankX(yE, zE, -1);
      if (xl >= 0) {
        setVox(xl, yE, zE, V_EYE);
        if (stalk) setVox(xl - 1, yE, zE, V_EYE);
      }
    }
  }

  // ======================= 5. pattern pass ================================
  // Countershading is always the base; the named pattern layers on top. Everything is
  // quantised to the three genome slots with no dithering — the greedy mesher merges runs
  // of identical colour, so a speckled shark is a shark that misses the triangle budget.
  const PAT = { solid: 0, countershaded: 1, stripes: 2, spots: 3, mottle: 4, leopard: 5 } as const;
  const patCode = PAT[g.pattern] ?? PAT.countershaded;
  const ps = clamp(g.patternScale, 0.02, 0.3);
  const bands = Math.max(3, Math.round(3 + g.length * 0.16));

  for (let z = 0; z < l; z++) {
    const i = clamp(zNose - z, 0, N - 1);
    const cyP = yArr[i] + yOff;
    const ryP = Math.max(0.8, rArr[i] * vArr[i]);
    const u = i / span1;
    const stripe = Math.sin(u * bands * Math.PI * 2);
    const zBase = wh * z;
    for (let y = 0; y < h; y++) {
      const rowBase = zBase + w * y;
      const rel = (y - cyP) / ryP;
      for (let x = 0; x < w; x++) {
        if (data[rowBase + x] !== V_FLANK) continue;
        const n = nz.noise(x * ps, y * ps, z * ps);
        let slot: number;
        if (patCode === PAT.solid) {
          slot = rel < -0.52 + n * 0.1 ? V_VENTRAL : V_FLANK;
        } else {
          // noise-perturbed so the countershading boundary is a ragged waterline, not a plane
          slot = rel > 0.28 + n * 0.14 ? V_DORSAL : rel < -0.3 + n * 0.12 ? V_VENTRAL : V_FLANK;
          if (slot !== V_VENTRAL) {
            if (patCode === PAT.stripes) {
              if (stripe + n * 0.35 > 0.15) slot = V_DORSAL;
            } else if (patCode === PAT.spots) {
              if (nz.noise(x * ps * 2.1 + 31.7, y * ps * 2.1, z * ps * 2.1) > 0.42) slot = V_DORSAL;
            } else if (patCode === PAT.mottle) {
              const f = nz.fbm(x * ps, y * ps, z * ps, 2);
              if (slot === V_FLANK && f > 0.12) slot = V_DORSAL;
              else if (slot === V_DORSAL && f < -0.2) slot = V_FLANK;
            } else if (patCode === PAT.leopard) {
              if (nz.ridge(x * ps * 1.3, y * ps * 1.3, z * ps * 1.3, 2) > 0.68) slot = V_DORSAL;
            }
          }
        }
        data[rowBase + x] = slot;
      }
    }
  }

  // ======================= 6. bounds ======================================
  let minX = w;
  let minY = h;
  let minZ = l;
  let maxX = -1;
  let maxY = -1;
  let maxZ = -1;
  let solidCount = 0;
  for (let z = 0; z < l; z++) {
    const zBase = wh * z;
    for (let y = 0; y < h; y++) {
      const rowBase = zBase + w * y;
      for (let x = 0; x < w; x++) {
        if (data[rowBase + x] === 0) continue;
        solidCount++;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
        if (z < minZ) minZ = z;
        if (z > maxZ) maxZ = z;
      }
    }
  }

  // A genome degenerate enough to sculpt nothing would hand GreedyMesh empty bounds and
  // give three.js a NaN bounding sphere. One voxel is cheaper than defending downstream.
  if (solidCount === 0) {
    const x = cx;
    const y = Math.min(h - 1, Math.max(0, Math.round(yOff)));
    const z = Math.min(l - 1, Math.max(0, zNose));
    data[x + w * y + wh * z] = V_FLANK;
    solidCount = 1;
    minX = maxX = x;
    minY = maxY = y;
    minZ = maxZ = z;
  }

  return {
    dims,
    data,
    solidCount,
    min: [minX, minY, minZ],
    max: [maxX, maxY, maxZ],
  };
}
